import React, { useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeList, getTheme } from '../config/themes';
import { Check } from 'lucide-react';

/**
 * Visual theme picker — personal (Settings). Select then Apply.
 */
const ThemePicker = ({ className = '' }) => {
  const {
    activeThemeId,
    userThemeId,
    platformThemeId,
    setUserTheme,
    clearUserTheme,
    setPreviewTheme,
    clearPreviewTheme,
  } = useTheme();

  const themes = getThemeList();
  const appliedId = userThemeId || platformThemeId || activeThemeId;
  const [pendingId, setPendingId] = useState(appliedId);
  const [message, setMessage] = useState('');

  useEffect(() => {
    setPendingId(appliedId);
  }, [appliedId]);

  const hasChanges = pendingId !== appliedId;

  const handlePick = (themeId) => {
    setPendingId(themeId);
    setPreviewTheme(themeId);
    setMessage('Previewing theme. Click Apply to save your personal preference.');
  };

  const handleApply = () => {
    clearPreviewTheme();
    if (pendingId === platformThemeId && userThemeId) {
      clearUserTheme();
      setMessage('Reset to platform default.');
    } else if (pendingId !== appliedId) {
      setUserTheme(pendingId);
      setMessage('Personal theme saved on this device.');
    }
    setTimeout(() => setMessage(''), 3500);
  };

  const handleCancel = () => {
    setPendingId(appliedId);
    clearPreviewTheme();
    setMessage('');
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {message && (
        <p className="text-xs text-[var(--pf-ink-muted)] rounded-lg bg-[var(--pf-accent-soft)] px-3 py-2">
          {message}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {themes.map((theme) => {
          const isPending = pendingId === theme.id;
          const isApplied = appliedId === theme.id;
          const isDraft = theme.status === 'draft';
          return (
            <button
              key={theme.id}
              type="button"
              onClick={() => handlePick(theme.id)}
              className={`text-left p-4 rounded-2xl border-2 transition-all ${
                isPending
                  ? 'border-[var(--pf-accent)] bg-[var(--pf-accent-soft)] shadow-sm'
                  : 'border-[var(--pf-border)] bg-[var(--pf-surface)] hover:border-[var(--pf-mint-deep)]'
              } ${isDraft ? 'opacity-90' : ''}`}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  <p className="font-semibold text-[var(--pf-ink)] text-sm">{theme.name}</p>
                  {isApplied && !hasChanges && (
                    <span className="text-[0.65rem] uppercase tracking-wide font-bold text-[var(--pf-ink-muted)]">
                      Current
                    </span>
                  )}
                  {isDraft && (
                    <span className="text-[0.65rem] uppercase tracking-wide font-bold text-[var(--pf-ink-muted)]">
                      Draft slot
                    </span>
                  )}
                </div>
                {isPending && (
                  <span className="w-6 h-6 rounded-full bg-[var(--pf-accent)] text-[var(--pf-accent-text)] flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </span>
                )}
              </div>
              <div className="flex gap-1.5 mb-2">
                {theme.swatches.map((color) => (
                  <span
                    key={color}
                    className="w-7 h-7 rounded-lg border border-black/5"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <p className="text-xs text-[var(--pf-ink-muted)] leading-relaxed">{theme.description}</p>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {hasChanges && (
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 rounded-xl border border-[var(--pf-border)] text-sm font-medium text-[var(--pf-ink-muted)] hover:bg-[var(--pf-canvas)]"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={handleApply}
          disabled={!hasChanges}
          className="pf-btn-primary px-5 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Apply theme
        </button>
        {userThemeId && (
          <button
            type="button"
            onClick={() => {
              clearUserTheme();
              setPendingId(platformThemeId);
              setMessage(`Using platform default (${getTheme(platformThemeId).name}).`);
              setTimeout(() => setMessage(''), 3500);
            }}
            className="text-sm font-medium text-[var(--pf-ink-muted)] hover:text-[var(--pf-ink)] underline-offset-2 hover:underline ml-auto"
          >
            Use platform default ({getTheme(platformThemeId).name})
          </button>
        )}
      </div>
    </div>
  );
};

export default ThemePicker;
